import {Attributes, AttributesRow, Card, DataStatus} from 'platform/components';
import {HStack, Icon, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {GetArticleUpdateJobResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getUserName} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, RequiredTestIdProps} from 'shared/web';

interface ApprovalExecutionDetailsTabProps extends RequiredTestIdProps {
  articleUpdateJob: GetArticleUpdateJobResponse | Nullish;
}

export function ApprovalExecutionDetailsTab(props: ApprovalExecutionDetailsTabProps) {
  const formatDate = useDateTimeFormatter();

  const approvedByUserQuery = useGetUserQuery(
    {id: props.articleUpdateJob?.approvedBy as string},
    {skip: isNil(props.articleUpdateJob?.approvedBy)}
  );

  const plannedByUserQuery = useGetUserQuery(
    {id: props.articleUpdateJob?.plannedBy as string},
    {skip: isNil(props.articleUpdateJob?.plannedBy)}
  );

  const formatToDateTime = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateTimeMedium', parseDate(date));
  };

  const isLoading = approvedByUserQuery.isLoading || plannedByUserQuery.isLoading;

  const approvalInformation: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.approved'),
      content: (
        <HStack spacing={1} align="center">
          <Icon
            value={props.articleUpdateJob?.isApproved ? 'action/check_circle' : 'navigation/cancel'}
            color={props.articleUpdateJob?.isApproved ? 'severity.success' : 'severity.danger'}
            size={4}
          />
          <Text size="small">
            {props.articleUpdateJob?.isApproved
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no')}
          </Text>
        </HStack>
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.approvedBy'),
      value: getUserName(approvedByUserQuery.data),
    },
    {
      label: i18n.t('entity.warehouse.labels.approvedAt'),
      value: formatToDateTime(props.articleUpdateJob?.approvedAt),
    },
  ];

  const executionInformation: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.planned'),
      content: (
        <HStack spacing={1} align="center">
          <Icon
            value={props.articleUpdateJob?.isPlanned ? 'action/check_circle' : 'navigation/cancel'}
            color={props.articleUpdateJob?.isPlanned ? 'severity.success' : 'severity.danger'}
            size={4}
          />
          <Text size="small">
            {props.articleUpdateJob?.isPlanned
              ? i18n.t('general.labels.yes')
              : i18n.t('general.labels.no')}
          </Text>
        </HStack>
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.plannedDatetime'),
      value: formatToDateTime(props.articleUpdateJob?.plannedExecutionAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.plannedBy'),
      value: getUserName(plannedByUserQuery.data),
    },
    {
      label: i18n.t('entity.warehouse.labels.executedAt'),
      value: formatToDateTime(props.articleUpdateJob?.executedAt),
    },
    {
      label: i18n.t('entity.warehouse.labels.completedAt'),
      value: formatToDateTime(props.articleUpdateJob?.completedAt),
    },
  ];

  return (
    <Card hasSeparator title={i18n.t('entity.warehouse.labels.approvalExecutionDetails')}>
      <DataStatus isLoading={isLoading} minHeight={104}>
        <VStack spacing={4} data-testid={props['data-testid']}>
          <Card variant="inlineGrey" title={i18n.t('entity.warehouse.labels.approvalInformation')}>
            <Attributes size="quarter" rows={approvalInformation} />
          </Card>
          <Card variant="inlineGrey" title={i18n.t('entity.warehouse.labels.executionInformation')}>
            <Attributes size="quarter" rows={executionInformation} />
          </Card>
        </VStack>
      </DataStatus>
    </Card>
  );
}
